import { memo } from 'react'
import { useContractPurchaseAiCreditPage } from './useContractPurchaseAiCreditPage'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { ValueChangedEvent as NumberBoxValueChangedEvent } from 'devextreme/ui/number_box'
import { BillingCycle } from '@crew/enums/app'
import { useTranslation } from '@crew/modules/i18n'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { useCallback, useMemo } from 'react'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useToast } from 'hooks/useToast'
import {
  useGetAiCreditPriceTiersQuery,
  useGetAiCreditRemainingQuery,
  useGetBillingCycleQuery,
} from '@crew/apis/contract/contractApis'
import { FormValues } from './useContractPurchaseAiCreditPage'
import { useWatch } from 'react-hook-form'
import { CrewNumberBoxField } from 'components/forms/crewNumberBoxField'
import { GetAiCreditPriceTiersResponse_AiCreditPriceTier } from '@crew/apis/contract/models/getAiCreditPriceTiers/response'

// TODO: 英語表記にした際に崩れるので要対応
// https://break-tmc.atlassian.net/browse/CREW-14306

/**
 * AIクレジット購入ページ
 */
export const ContractPurchaseAiCreditPage = memo(() => {
  const {
    control,
    handleSubmit,
    setValue,
    updateAiCredit,
    isLoadingUpdateAiCreditCount,
  } = useContractPurchaseAiCreditPage()

  const { historyBack, navigate } = useCrewNavigate()
  const { t } = useTranslation()
  const [showApiErrors] = useShowApiErrors()
  const toast = useToast()

  // AIクレジットの単価を取得
  const {
    data: aiCreditPriceTierData,
    isFetching: isAiCreditPriceTierFetching,
  } = useGetAiCreditPriceTiersQuery()

  // 請求サイクル取得
  const { data: billingCycleData } = useGetBillingCycleQuery()

  // 残りのAIクレジット数を取得
  const {
    data: aiCreditRemainingCountData,
    isFetching: isAiCreditRemainingCountFetching,
  } = useGetAiCreditRemainingQuery()

  // AIクレジットの総契約数量（無料枠＋Stripeの数量）
  const totalSubscriptionCount =
    (aiCreditPriceTierData?.freeAiCreditCount ?? 0) +
    (aiCreditPriceTierData?.subscriptionAiCreditCount ?? 0)

  // 入力されているAIクレジット数
  const inputtedAiCreditCount = useWatch({
    control,
    name: 'aiCredit',
  })

  // AIクレジットの金額を計算する関数
  const calculateAiCreditAmount = useCallback(
    (
      creditCount: number,
      tiers: GetAiCreditPriceTiersResponse_AiCreditPriceTier[]
    ) => {
      // 引数のクレジット数に該当する単価を格納する変数
      let targetTier:
        | GetAiCreditPriceTiersResponse_AiCreditPriceTier
        | undefined = undefined

      // ティアごとに単価が異なるので、ティア内から該当する単価を探す
      for (const tier of tiers) {
        if (tier.upTo === 0) {
          // upToが0の場合は、最上位のティアなのでその単価を使用する
          targetTier = tier
          break
        } else if (creditCount > tier.upTo) {
          // upToよりも引数のクレジット数が多い場合は、さらに上のティアを探すためcontinueする
          continue
        } else if (creditCount <= tier.upTo) {
          // 該当するティアを見つけたらその単価を使用
          targetTier = tier
          break
        }
      }

      // 通常ありえないが、該当するティアが見つからなかった場合は何も返さない（undefinedとなる）
      if (!targetTier) {
        return
      }

      // 単価とクレジット数を掛け算して金額を計算
      return targetTier.unitAmountDecimal * creditCount
    },
    []
  )

  // 変更するボタンクリック時の処理
  const handleChangeButtonClick = useCallback(() => {
    // react-hook-formのhandleSubmitに渡すコールバック関数を定義する
    const onSubmit = async (data: FormValues) => {
      try {
        //
        await updateAiCredit(data.aiCredit)

        toast.success(t('message.contract.purchaseAiCreditSuccess'))

        // 契約ポータル画面へ遷移
        navigate('/contract/portal')
      } catch (error) {
        showApiErrors(error)
      }
    }

    // 変更実行
    handleSubmit(onSubmit)()
  }, [handleSubmit, updateAiCredit, toast, t, navigate, showApiErrors])

  // キャンセルボタンクリック時の処理
  const handleCancelButtonClick = useCallback(() => {
    // 前の画面（契約ポータル画面）に戻る
    historyBack()
  }, [historyBack])

  // ナンバーボックスの値変更時の処理、10単位での入力にする
  const handleNumberOfAiCreditValueChanged = useCallback(
    (e: NumberBoxValueChangedEvent) => {
      if (e.value !== undefined) {
        // 10単位での入力にする
        const newValue = Math.floor(e.value / 10) * 10
        setValue('aiCredit', newValue)
      }
    },
    [setValue]
  )

  // 契約中の請求サイクル
  const currentBillingCycle = useMemo(() => {
    const currentBillingCycle = billingCycleData?.billingCycle.find((item) => {
      return item.isCurrentCycle === true
    })?.billingCycle

    // 型制約上undefined許容になるため型チェックを行う
    if (!currentBillingCycle) {
      return
    }

    return currentBillingCycle
  }, [billingCycleData?.billingCycle])

  // 契約中のAIクレジットの金額
  const currentAiCreditAmount = useMemo(() => {
    // 契約中の情報が取得できない場合は「-」
    if (!aiCreditPriceTierData || isAiCreditPriceTierFetching) {
      return '-'
    }

    // 現在契約しているAIクレジット数をもとに金額を計算する
    const price = calculateAiCreditAmount(
      aiCreditPriceTierData.subscriptionAiCreditCount,
      aiCreditPriceTierData.priceTiers
    )

    // 万が一金額が計算できなかった場合は「-」を表示する
    if (price === undefined) {
      return '-'
    }

    // 金額が0の場合は「無料」
    if (price === 0) {
      return t('label.free')
    }

    // {金額}{請求単位}の形式で金額表示
    return currentBillingCycle === BillingCycle.Month
      ? t('label.priceMonthlyText', {
          price: price,
        })
      : t('label.priceAnnualText', {
          price: price,
        })
  }, [
    aiCreditPriceTierData,
    isAiCreditPriceTierFetching,
    calculateAiCreditAmount,
    currentBillingCycle,
    t,
  ])

  // 変更後のAIクレジットの金額を計算して表示する
  const upgradedAiCreditAmount = useMemo(() => {
    // 変更後の情報が取得できない場合は「-」
    if (!aiCreditPriceTierData || isAiCreditPriceTierFetching) {
      return ''
    }

    const calcAmount = calculateAiCreditAmount(
      inputtedAiCreditCount,
      aiCreditPriceTierData.priceTiers
    )

    if (calcAmount === undefined) {
      // 該当するティアが見つからなかった場合は何も表示しない
      return ''
    }

    const price = calcAmount.toLocaleString()

    // {金額}{請求単位}の形式で金額表示
    return currentBillingCycle === BillingCycle.Month
      ? t('label.priceMonthlyText', {
          price: price,
        })
      : t('label.priceAnnualText', {
          price: price,
        })
  }, [
    calculateAiCreditAmount,
    currentBillingCycle,
    isAiCreditPriceTierFetching,
    aiCreditPriceTierData,
    inputtedAiCreditCount,
    t,
  ])

  // 現在のAIクレジット使用状況を表示する
  const currentAiCreditUsage = useMemo(() => {
    // フェッチ中であったりデータが取得できていない場合は「-」を表示する
    if (
      isAiCreditPriceTierFetching ||
      !aiCreditPriceTierData ||
      isAiCreditRemainingCountFetching ||
      !aiCreditRemainingCountData
    ) {
      return '-'
    }

    // 現在の使用量は、契約容量から残りのAIクレジット数を引いた値
    // 例: 現在のAIクレジット数が1000、残りのAIクレジット数が200の場合、使用量は800
    const currentUsage =
      totalSubscriptionCount - aiCreditRemainingCountData.remainingAiCreditCount

    return currentUsage + ' / ' + totalSubscriptionCount
  }, [
    aiCreditPriceTierData,
    aiCreditRemainingCountData,
    isAiCreditPriceTierFetching,
    isAiCreditRemainingCountFetching,
    totalSubscriptionCount,
  ])

  return (
    <form>
      <div className="flex flex-col gap-y-2.5 mx-auto w-full max-w-2xl">
        <div className="flex py-2.5 w-full flex-col justify-start items-start gap-y-2.5">
          <span className="text-md font-bold">
            {t('label.contractPurchaseAiCredit.title')}
          </span>
          <div className="flex-col w-full justify-start items-start gap-y-2.5 inline-flex">
            <div className="flex-col flex gap-y-1.5">
              {/* AIクレジット購入の説明文 */}
              <div>{t('label.contractPurchaseAiCredit.description')}</div>
              <div className="text-crew-red-3-light dark:text-crew-red-2-dark">
                {t('label.contractPurchaseAiCredit.usageNote')}
              </div>

              {/* AIクレジット使用状況 */}
              <div className="flex gap-x-1 text-crew-blue-3-light dark:text-crew-blue-2-dark">
                <div>
                  {t('label.usageStatusOfTarget', {
                    target: t('label.aiCredit'),
                  }) + ': '}
                </div>
                {/* 値 */}
                <div>{currentAiCreditUsage}</div>
                {/* 使用済 */}
                <div>{t('label.alreadyUsed')}</div>
              </div>
            </div>

            {/* 現在 */}
            <div className="w-full justify-start items-center gap-x-2.5 inline-flex">
              {/* 「現在」のラベル */}
              <div className="w-20 self-stretch justify-start items-center flex">
                <div className="crew-text-gray-4">{t('label.contractNow')}</div>
              </div>

              {/* 組織全体で毎月○○クレジットまで使用可能 */}
              <div className="flex grow shrink gap-x-1 justify-start items-center">
                <div className="crew-text-gray-4">
                  {t(
                    'label.contractPurchaseAiCredit.acrossTheOrganizationPerMonth'
                  )}
                  {/* [無料枠のAIクレジット数] + [Stripeで購入しているAIクレジット数] */}
                  {!isAiCreditPriceTierFetching && aiCreditPriceTierData
                    ? `${aiCreditPriceTierData.freeAiCreditCount} + ${aiCreditPriceTierData.subscriptionAiCreditCount}`
                    : '-'}
                  {t('label.contractPurchaseAiCredit.canBeUse')}
                </div>
              </div>

              {/* 金額 */}
              <div className="self-stretch justify-end items-center flex">
                <div className=" text-crew-blue-3-light dark:text-crew-blue-2-dark">
                  {currentAiCreditAmount}
                </div>
              </div>
            </div>

            {/* 変更後 */}
            <div className="self-stretch justify-start items-center gap-x-2.5 inline-flex">
              {/* 「変更後」のラベル */}
              <div className="w-20 self-stretch justify-start items-center flex">
                <div className="crew-text-gray-4">
                  {t('label.contractAfterChange')}
                </div>
              </div>

              {/* 組織全体で毎月○○クレジットまで使用可能 */}
              <div className="grow shrink">
                <div className="flex gap-x-1 justify-start items-center">
                  <div className="crew-text-gray-4 justify-center">
                    {t(
                      'label.contractPurchaseAiCredit.acrossTheOrganizationPerMonth'
                    )}
                    {/* 無料枠のAIクレジット数 */}
                    {!isAiCreditPriceTierFetching && aiCreditPriceTierData
                      ? `${aiCreditPriceTierData.freeAiCreditCount} + `
                      : '-'}
                  </div>
                  <CrewNumberBoxField
                    control={control}
                    id="aiCredit"
                    name="aiCredit"
                    step={10}
                    showLabel={false}
                    width={100}
                    className="mx-1"
                    onValueChanged={handleNumberOfAiCreditValueChanged}
                    min={0}
                    showSpinButtons={true}
                  />

                  <div className="crew-text-gray-4">
                    {t('label.contractPurchaseAiCredit.canBeUse')}
                  </div>
                </div>
                <div className="text-crew-red-3-light dark:text-crew-red-2-dark text-sm leading-5">
                  {t('label.contractPurchaseAiCredit.canBeChangeIncrements', {
                    value: 10,
                  })}
                </div>
              </div>

              {/* 金額 */}
              <div className="self-stretch justify-end items-center flex">
                <div className=" text-crew-blue-3-light dark:text-crew-blue-2-dark">
                  {upgradedAiCreditAmount}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-row gap-x-2.5 items-center">
          {/* 「変更する」ボタン */}
          <CrewButton
            text={t('action.change')}
            type="primary"
            onClick={handleChangeButtonClick}
            disabled={
              isAiCreditPriceTierFetching || isLoadingUpdateAiCreditCount
            }
          />

          {/* 「キャンセル」ボタン */}
          <CrewButton
            text={t('action.cancel')}
            type="normal"
            stylingMode="outlined"
            onClick={handleCancelButtonClick}
          />
        </div>
      </div>
    </form>
  )
})
